import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invokeEdgeFunction } from '../services/edgeFunctions';
import { errorMessage, isRecord } from '../services/errors';
import { tapLink } from '../components/styles';

/**
 * Where Slack sends the user back after they approve (or cancel) connecting.
 * The code and state are read once and removed from the address bar; the
 * server accepts the state only if it issued it to this user, unused and
 * unexpired, and only then exchanges the code.
 */
export default function SlackCallbackPage() {
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [link] = useState(() => ({ code: params.get('code'), state: params.get('state'), error: params.get('error') }));
  const started = useRef(false);

  const connect = useMutation({
    mutationFn: async () => {
      const result = await invokeEdgeFunction('slack-integration', { action: 'oauth', code: link.code, state: link.state });
      return isRecord(result) && typeof result.team_name === 'string' ? result.team_name : null;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integration-connections'] }),
  });

  useEffect(() => {
    // The code and state are single-use: keep them out of history, reloads and shared links.
    window.history.replaceState(null, '', window.location.pathname);
    if (started.current) return;
    started.current = true;
    if (!link.error && link.code && link.state) connect.mutate();
    // Runs once, for the address the page opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let message: string;
  let tone = 'text-on-surface';
  if (link.error) {
    message = link.error === 'access_denied' ? 'Slack was not connected: you cancelled on Slack.' : `Slack was not connected: Slack said "${link.error}".`;
    tone = 'text-error';
  } else if (!link.code || !link.state) {
    message = 'This page needs the code and state Slack sends back. Start connecting again from Integrations.';
    tone = 'text-error';
  } else if (connect.isError) {
    message = `Slack was not connected: ${errorMessage(connect.error)}`;
    tone = 'text-error';
  } else if (connect.isSuccess) {
    message = connect.data ? `Connected to ${connect.data}.` : 'Slack is connected.';
    tone = 'text-success';
  } else {
    message = 'Connecting Slack…';
  }

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold">Slack</h1>
      <p role="status" className={tone}>
        {message}
      </p>
      <Link to="/integrations" className={`${tapLink} text-primary font-medium hover:underline`}>
        Back to Integrations
      </Link>
    </div>
  );
}
